import React from 'react';
import { intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { humanizeLineItemCode } from '../../util/data';
import { LINE_ITEMS, propTypes } from '../../util/types';

import {
  ToolTip,
} from '../../components';

import css from './OrderBreakdown.module.css';

/**
 * Helper to extract adjustmentId from line item code
 * Supports both formats:
 * - line-item/price-adjustment/12345 (new format with embedded ID)
 * - line-item/price-adjustment (legacy format)
 */
const extractAdjustmentId = (code) => {
  if (!code) return null;
  const match = code.match(/^line-item\/price-adjustment\/(\d+)$/);
  return match ? parseInt(match[1], 10) : null;
};

/**
 * Check if a line item code is a price adjustment
 */
const isPriceAdjustmentCode = (code) => {
  if (!code) return false;
  return code === 'line-item/price-adjustment' || 
         code.startsWith('line-item/price-adjustment/');
};

/**
 * Find matching adjustment from protectedData
 * Priority: 1) Match by adjustmentId, 2) Fall back to amount matching
 */
const findMatchingAdjustment = (item, priceAdjustments, isProvider, usedAdjustmentIds) => {
  if (!priceAdjustments || priceAdjustments.length === 0) return null;

  // First, try to match by adjustmentId embedded in the code
  const embeddedId = extractAdjustmentId(item.code);
  if (embeddedId) {
    const matchById = priceAdjustments.find(adj => adj.adjustmentId === embeddedId);
    if (matchById) {
      return matchById;
    }
  }

  // Fall back to amount matching (for legacy line items without embedded ID)
  const targetAmount = isProvider 
    ? item.unitPrice.amount / 0.8  // Reverse the 80% commission applied earlier
    : item.unitPrice.amount;

  // Find adjustment by amount that hasn't been used yet
  const matchByAmount = priceAdjustments.find((adj) => {
    const amountMatches = Math.abs(adj.amount - targetAmount) < 1; // Allow for rounding
    const notUsed = !usedAdjustmentIds.has(adj.adjustmentId);
    return amountMatches && notUsed;
  });

  return matchByAmount;
};

/**
 * Renders non-reversal line items that are not listed in the
 * `LINE_ITEMS` array in util/types.js
 *
 * The line items are rendered so that the line item code is formatted to human
 * readable form and the line total is printed as price.
 *
 * If you require another kind of presentation for your line items, add them to
 * the `LINE_ITEMS` array in util/types.js and create a specific line item
 * component for them that can be used in the `OrderBreakdown` component.
 *
 * @component
 * @param {Object} props
 * @param {Array<propTypes.lineItem>} props.lineItems - The line items to render
 * @param {boolean} props.isProvider - Whether the provider is the one receiving the commission
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const LineItemUnknownItemsMaybe = props => {
  const { lineItems, isProvider, intl, transaction } = props;

  // Get price adjustments from transaction protectedData
  const priceAdjustments = transaction?.attributes?.protectedData?.priceAdjustments || [];

  // Track which adjustments have been matched (for amount-based fallback)
  const usedAdjustmentIds = new Set();

  // Resolve unknown non-reversal line items, excluding pet pricing items
  const allItems = lineItems.filter(item => {
    // Check if it's an unknown item (not in LINE_ITEMS) OR a price adjustment
    const isUnknown = LINE_ITEMS.indexOf(item.code) === -1;
    const isPriceAdj = isPriceAdjustmentCode(item.code);
    const isNotReversal = !item.reversal;
    const isNotPetPricing = !(item.code.includes('base-') || item.code.includes('additional-')) || 
                           !(item.code.includes('-dogs') || item.code.includes('-puppies') || item.code.includes('-cats'));
    return (isUnknown || isPriceAdj) && isNotReversal && isNotPetPricing;
  });

  const items = isProvider
    ? allItems.filter(item => item.includeFor.includes('provider'))
    : allItems.filter(item => item.includeFor.includes('customer'));

  return items.length > 0 ? (
    <React.Fragment>
      {items.map((item, i) => {
        const quantity = item.quantity;

        let label =
          quantity && quantity > 1
            ? `${humanizeLineItemCode(item.code)} x ${quantity}`
            : humanizeLineItemCode(item.code);

        let popover = null;

        // Handle gratuity/tip line items
        if (item.code === 'line-item/gratuity') {
          label = `Elected Tip (${item.percentage.toString()}%)`;
          popover = (
            <ToolTip
              tip={(
                <span>
                  Tip percentage is elected by the client and is calculated based on the 
                  Rate on Profile amount set in your Listing.<br />
                  100% of tips go to the pet sitter.
                </span>
              )}
              isSuperScript
            />
          );
        }

        // Handle price adjustment line items
        if (isPriceAdjustmentCode(item.code)) {
          const adjustment = findMatchingAdjustment(
            item, 
            priceAdjustments, 
            isProvider, 
            usedAdjustmentIds
          );
          
          if (adjustment) {
            // Mark this adjustment as used (for amount-based fallback matching)
            usedAdjustmentIds.add(adjustment.adjustmentId);
            
            // Use the custom label from the adjustment
            if (adjustment.label) {
              label = adjustment.label;
            }
          }
        }

        const formattedTotal = formatMoney(intl, item.lineTotal);
        
        return (
          <div key={`${i}-${item.code}`} className={css.lineItem}>
            <span className={css.itemLabel}>{label}</span>
            <span className={css.itemValue}>{formattedTotal}{popover}</span>
          </div>
        );
      })}
    </React.Fragment>
  ) : null;
};

export default LineItemUnknownItemsMaybe;
